<template>
  <ListComponent title="Trailer Type" :totalCount="totalItems">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="3" class="px-0 d-flex align-center">
          <v-text-field
            label="Search"
            class="search-field"
            dense
            outlined
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchTrailerType"
          />
        </v-col>
        <v-col v-if="totalItems > 0" cols="9" class="px-0 d-flex justify-end">
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>

        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 192px)"
            class="ag-theme-alpine"
            id="myGrid"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="allTrailerTypeList"
          />
        </v-col>
      </v-row>
    </template>

    <template #dialogs>
      <TrailerTypeForm
        v-model="showTrailerTypeForm"
        :editMode="isEditing"
        :editTrailerTypeID="editTrailerTypeID"
        @refreshList="itemsPerPageChanged(20)"
      />
    </template>

    <template #actions>
      <v-btn
        @click="addTrailerType"
        color="primary"
        depressed
        v-if="permissions && permissions.add"
      >
        <v-icon>mdi-plus</v-icon>
        <span>Add Trailer Type</span>
      </v-btn>
    </template>
  </ListComponent>
</template>

<script>
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import TrailerTypeButton from "@/components/AgGridComponents/TrailerTypeButton.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import TrailerTypeForm from "@/components/ModuleBased/dialogForms/TrailerTypeForm.vue";

import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    AgGridVue,
    Pagination,
    TrailerTypeButton,
    agColumnHeader: CustomHeader,
    TrailerTypeForm,
  },
  data() {
    return {
      sorting: {},
      headers: [
        {
          headerName: "Type",
          field: "type_name",
          sortable: true,
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "TrailerTypeButton",
          pinned: "right",
          width: 100,
        },
      ],
      allTrailerTypeList: [],

      // pagination vars
      itemsPerPage: 20,
      pageNo: 1,
      totalItems: 0,

      // filter vars
      filters: {},

      permissions: null,

      // Trailer Type Form
      showTrailerTypeForm: false,
      isEditing: false,
      editTrailerTypeID: null,

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
    };
  },

  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  methods: {
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchTrailerType() {
      this.pageNo = 1;
      this.getTrailerTypeList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getTrailerTypeList();
    },
    prevPage() {
      this.pageNo--;
      this.getTrailerTypeList();
    },
    nextPage() {
      this.pageNo++;
      this.getTrailerTypeList();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },
    getTrailerTypeList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.trailerType
        .getTrailerTypeList(params)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.allTrailerTypeList = res.data;
          this.totalItems = res.count;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    addTrailerType() {
      this.editTrailerTypeID = null;
      this.isEditing = false;
      this.showTrailerTypeForm = true;
    },
    editTrailerType(id) {
      this.editTrailerTypeID = id;
      this.isEditing = true;
      this.showTrailerTypeForm = true;
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      let url = localStorage.getItem("user_type").toLowerCase();
      if (url == "admin") {
        url = "admin_company";
      }
      this.$router.push(`/${url}`);
    } else {
      const permissions = getPermissions("lane");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        let url = localStorage.getItem("user_type").toLowerCase();
        if (url == "admin") {
          url = "admin_company";
        }
        this.$router.push(`/${url ? url : "error"}`);
      } else {
        this.permissions = { ...permissions };
        this.getTrailerTypeList();
      }
    }
  },
};
</script>
